import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Scale } from 'lucide-react';
import { ptBR } from '@/i18n/pt-BR';
import { Badge, Blueprint, CardKicker, MonoData } from '@/components/ui';
import { AnalysisCanvas } from '@/components/AnalysisCanvas';
import { ApprovalSection } from '@/components/ApprovalSection';
import { BoundingBoxesEditor } from '@/components/BoundingBoxesEditor';
import {
  AnalysisError,
  fetchAnalysisBlob,
  getAnalysis,
  getAnalysisStatus,
  type AnalysisRecordData,
  type AnalysisResultData,
  type AnalysisStatus,
  type CriterionAssessmentData,
} from '@/lib/analyses';
import { useApiClient } from '@/lib/api';
import { CRITERION_STATUS_STYLE, lrBadgeClasses } from './AnalisePage.styles';

const POLL_INTERVAL_MS = 2000;

// Mapa criterion_id (1..10) -> chave i18n criterio.nome.*
const CRITERION_NAME_KEY: Record<number, keyof typeof ptBR.criterio.nome> = {
  1: 'calibre',
  2: 'inclinacao',
  3: 'proporcionalidade',
  4: 'espacamento',
  5: 'velocidade',
  6: 'pressao',
  7: 'ataque',
  8: 'minimos',
  9: 'gestos',
  10: 'forma',
};

function criterionName(id: number): string {
  const key = CRITERION_NAME_KEY[id];
  return key ? ptBR.criterio.nome[key] : `#${id}`;
}

function mapErrorToMessage(err: unknown): string {
  if (err instanceof AnalysisError) {
    if (err.reason === 'not_found') return ptBR.errors.analysisNotFound;
    if (err.reason === 'network') return ptBR.errors.networkOffline;
  }
  return ptBR.errors.generic;
}

// Hook de polling do status até atingir estado terminal -------------------

function useAnalysisLifecycle(analysisId: string | undefined) {
  const apiClient = useApiClient();
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [record, setRecord] = useState<AnalysisRecordData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (analysisId === undefined) {
      setError(ptBR.errors.analysisNotFound);
      return undefined;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      if (analysisId === undefined) return;
      try {
        const statusResp = await getAnalysisStatus(apiClient, analysisId);
        if (cancelled) return;
        setStatus(statusResp.status);
        if (statusResp.status === 'completed') {
          const full = await getAnalysis(apiClient, analysisId);
          if (cancelled) return;
          setRecord(full);
        } else if (statusResp.status !== 'failed') {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (cancelled) return;
        setError(mapErrorToMessage(err));
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [apiClient, analysisId]);

  return { status, record, error };
}

// Sub-componentes ---------------------------------------------------------

function ProgressView({ status }: { status: AnalysisStatus }) {
  const message =
    status === 'running' ? ptBR.analise.statusRunningMessage : ptBR.analise.statusPendingMessage;
  return (
    <section
      data-testid="analise-progress"
      aria-labelledby="analise-title"
      aria-live="polite"
      className="mx-auto max-w-2xl"
    >
      <h1 id="analise-title" className="text-display-md text-fg">
        {ptBR.analise.pageTitleAnalisando}
      </h1>
      <p className="mt-sc-4 text-body-md text-fg-muted">{message}</p>
      {/* Trilho reto sobre o ground, como a barra de progresso do upload (F7). */}
      <div
        className="mt-sc-6 h-1 w-full overflow-hidden border border-border"
        role="progressbar"
        aria-valuetext={message}
      >
        <div className="h-full w-1/3 animate-pulse bg-accent" />
      </div>
    </section>
  );
}

function FailedView({ message }: { message?: string }) {
  return (
    <section
      data-testid="analise-failed"
      aria-labelledby="analise-title"
      className="mx-auto max-w-2xl"
    >
      <h1 id="analise-title" className="text-display-md text-fg">
        {ptBR.analise.pageTitleErro}
      </h1>
      <p
        className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
        role="alert"
      >
        {message ?? ptBR.analise.statusFailedMessage}
      </p>
      <Link
        to="/casos"
        className="mt-sc-6 inline-block text-body-sm text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {ptBR.analise.backToUpload}
      </Link>
    </section>
  );
}

function VeredictoView({ result }: { result: AnalysisResultData }) {
  return (
    <section aria-labelledby="veredicto-title" className="mt-8">
      <h2 id="veredicto-title" className="text-heading-md text-fg">
        {ptBR.analise.veredictoSectionTitle}
      </h2>
      <Blueprint className="mt-sc-4 flex flex-col gap-sc-4 p-sc-6 sm:flex-row sm:items-center">
        <Scale
          className="h-12 w-12 shrink-0 text-accent-700"
          aria-hidden="true"
          strokeWidth={1.5}
        />
        <div className="flex-1">
          <CardKicker className="block">{ptBR.analise.veredictoLrLabel}</CardKicker>
          <div
            className="mt-sc-2 flex flex-wrap items-baseline gap-x-sc-4 gap-y-sc-2"
            data-testid="analise-lr"
          >
            {/* O LR é o dado que a tela existe para mostrar: mono, no degrau
                display do pacote. */}
            <MonoData className="text-display-lg leading-none text-fg">
              {result.likelihood_ratio.toExponential(3)}
            </MonoData>
            <Badge className={lrBadgeClasses(result.lr_classification)}>
              {ptBR.lr[result.lr_classification]}
            </Badge>
          </div>
        </div>
      </Blueprint>
      {result.likely_fraud_typology ? (
        <div className="mt-sc-4 flex items-start gap-sc-3 border border-danger bg-danger-50 px-sc-4 py-sc-3">
          <AlertTriangle
            className="mt-0.5 h-6 w-6 shrink-0 text-danger-700"
            aria-hidden="true"
            strokeWidth={1.5}
          />
          <div className="flex-1">
            <p className="font-mono text-[10px] uppercase leading-none tracking-[0.1em] text-danger-700">
              {ptBR.analise.veredictoTipologiaLabel}
            </p>
            <p
              className="mt-sc-2 text-body-md font-medium text-danger-700"
              data-testid="analise-tipologia"
            >
              {ptBR.fraude[result.likely_fraud_typology]}
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function HipotesesView({ result }: { result: AnalysisResultData }) {
  return (
    <section aria-labelledby="hipoteses-title" className="mt-8">
      <h2 id="hipoteses-title" className="text-heading-md text-fg">
        {ptBR.analise.hipotesesSectionTitle}
      </h2>
      <dl className="mt-sc-4 grid grid-cols-1 gap-sc-3 sm:grid-cols-[14rem_1fr]">
        <dt className="text-body-sm text-fg-muted">{ptBR.analise.hipoteseH1Label}</dt>
        <dd className="text-body-md text-fg" data-testid="analise-h1">
          {result.hypothesis_h1}
        </dd>
        <dt className="text-body-sm text-fg-muted">{ptBR.analise.hipoteseH2Label}</dt>
        <dd className="text-body-md text-fg" data-testid="analise-h2">
          {result.hypothesis_h2}
        </dd>
      </dl>
    </section>
  );
}

function CriterionCard({ criterion }: { criterion: CriterionAssessmentData }) {
  const style = CRITERION_STATUS_STYLE[criterion.status];
  const { Icon } = style;
  return (
    <Blueprint
      as="article"
      key={criterion.criterion_id}
      className={`border-l-4 p-sc-4 ${style.borderLeft}`}
      data-testid={`analise-criterio-${criterion.criterion_id}`}
    >
      <header className="flex items-center gap-sc-3">
        <Icon
          className={`h-5 w-5 shrink-0 ${style.iconClass}`}
          aria-hidden="true"
          strokeWidth={1.5}
        />
        <h3 className="flex-1 text-heading-sm text-fg">{criterionName(criterion.criterion_id)}</h3>
        <Badge className={style.badgeClass}>{ptBR.criterio.status[criterion.status]}</Badge>
      </header>
      <dl className="mt-sc-3 grid grid-cols-1 gap-sc-2 sm:grid-cols-3">
        <div>
          <dt className="text-caption text-fg-muted">{ptBR.analise.criterioPesoLabel}</dt>
          <dd className="text-body-sm text-fg">{ptBR.criterio.weight[criterion.weight]}</dd>
        </div>
        <div>
          <dt className="text-caption text-fg-muted">{ptBR.analise.criterioImpactoLabel}</dt>
          <dd className="text-body-sm text-fg">{ptBR.criterio.impact[criterion.impact]}</dd>
        </div>
        <div>
          <dt className="text-caption text-fg-muted">{ptBR.analise.criterioRefsLabel}</dt>
          <dd className="text-body-sm text-fg">{criterion.references.join(', ')}</dd>
        </div>
      </dl>
      <p className="mt-sc-3 text-body-sm text-fg">
        <span className="font-medium text-fg-muted">{ptBR.analise.criterioEvidenciaLabel}:</span>{' '}
        {criterion.evidence_text}
      </p>
      {/* Coordenada é dado pericial: mono, como toda medida da mesa. */}
      <MonoData className="mt-1 block text-caption text-fg-dim">
        {`${ptBR.analise.criterioCoordsLabel}: ${criterion.evidence_coords
          .map(
            (bbox) =>
              `(x=${bbox.x.toFixed(0)}, y=${bbox.y.toFixed(0)}, w=${bbox.width.toFixed(0)}, h=${bbox.height.toFixed(0)})`,
          )
          .join('; ')}`}
      </MonoData>
    </Blueprint>
  );
}

function CriteriosView({ result }: { result: AnalysisResultData }) {
  return (
    <section aria-labelledby="criterios-title" className="mt-8">
      <h2 id="criterios-title" className="text-heading-md text-fg">
        {ptBR.analise.criteriosSectionTitle}
      </h2>
      <div className="mt-sc-4 flex flex-col gap-sc-4">
        {result.criteria.map((c) => (
          <CriterionCard key={c.criterion_id} criterion={c} />
        ))}
      </div>
    </section>
  );
}

function useAnalysisBlobs(analysisId: string) {
  const apiClient = useApiClient();
  const [refUrl, setRefUrl] = useState<string | null>(null);
  const [questUrl, setQuestUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdRef: string | null = null;
    let createdQuest: string | null = null;

    async function load() {
      try {
        const [refBlob, questBlob] = await Promise.all([
          fetchAnalysisBlob(apiClient, analysisId, 'ref'),
          fetchAnalysisBlob(apiClient, analysisId, 'quest'),
        ]);
        if (cancelled) return;
        createdRef = URL.createObjectURL(refBlob);
        createdQuest = URL.createObjectURL(questBlob);
        setRefUrl(createdRef);
        setQuestUrl(createdQuest);
      } catch {
        // Falha silenciosa — o veredicto + critérios ainda são úteis.
        // E4.F1 substitui as <img> por canvas Fabric.js com tratamento explícito.
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (createdRef !== null) URL.revokeObjectURL(createdRef);
      if (createdQuest !== null) URL.revokeObjectURL(createdQuest);
    };
  }, [apiClient, analysisId]);

  return { refUrl, questUrl };
}

function SignaturePair({ record }: { record: AnalysisRecordData }) {
  const { refUrl, questUrl } = useAnalysisBlobs(record.id);
  return (
    <section data-testid="analise-imagens" aria-labelledby="imagens-title" className="mt-6">
      <h2 id="imagens-title" className="text-heading-md text-fg">
        {ptBR.analise.imagensSectionTitle}
      </h2>
      {/* Os dois espécimes como figuras de blueprint, com legenda mono — a
          mesma família da moldura da mesa e da página do documento. */}
      <div className="mt-sc-4 grid grid-cols-1 gap-sc-4 md:grid-cols-2">
        <Blueprint as="figure" className="bg-paper p-sc-3">
          <figcaption>
            <MonoData variant="label">{ptBR.analise.imagemReferenciaLabel}</MonoData>
          </figcaption>
          <div className="mt-sc-2">
            <AnalysisCanvas
              imageUrl={refUrl}
              boundingBoxes={[]}
              altText={ptBR.analise.imagemReferenciaAlt}
              testId="analise-canvas-ref"
            />
          </div>
        </Blueprint>
        <Blueprint as="figure" className="bg-paper p-sc-3">
          <figcaption>
            <MonoData variant="label">{ptBR.analise.imagemQuestionadaLabel}</MonoData>
          </figcaption>
          <div className="mt-sc-2">
            <BoundingBoxesEditor
              record={record}
              imageUrl={questUrl}
              altText={ptBR.analise.imagemQuestionadaAlt}
            />
          </div>
        </Blueprint>
      </div>
    </section>
  );
}

function CompletedView({ record }: { record: AnalysisRecordData }) {
  const result = record.result;
  if (result === null) {
    // status=completed sem result é estado impossível — defensive guard.
    return <FailedView />;
  }
  return (
    <section
      data-testid="analise-resultado"
      aria-labelledby="analise-title"
      className="mx-auto max-w-3xl"
    >
      <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">{ptBR.analise.eyebrow}</p>
      <h1 id="analise-title" className="text-display-md text-fg">
        {ptBR.analise.pageTitleResultado}
      </h1>
      <SignaturePair record={record} />
      <VeredictoView result={result} />
      <HipotesesView result={result} />
      <CriteriosView result={result} />
      <ApprovalSection record={record} />
    </section>
  );
}

// Página principal --------------------------------------------------------

export function AnalisePage() {
  const { id } = useParams<{ id: string }>();
  const { status, record, error } = useAnalysisLifecycle(id);

  if (error !== null) {
    return <FailedView message={error} />;
  }
  if (status === null) {
    return <ProgressView status="pending" />;
  }
  if (status === 'failed') {
    return <FailedView />;
  }
  if (status === 'completed' && record !== null) {
    return <CompletedView record={record} />;
  }
  return <ProgressView status={status} />;
}
